"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import Image from "next/image";
import {
  generateBlurDataUrl,
  buildSupabaseTransformedUrl,
} from "@/lib/imageUtils";

const SIZE_BREAKPOINTS = [80, 160, 240, 320, 480, 640, 960, 1200, 1600];
const STRICT_FEED_BREAKPOINTS = [64, 96, 192, 400, 600];
const THUMBNAIL_BREAKPOINTS = [40, 56, 64, 80, 96, 120];
const DETAIL_MAIN_BREAKPOINTS = [600, 960, 1200];
const DETAIL_ZOOM_BREAKPOINTS = [960, 1200, 1600];

type ImageUsage =
  | "default"
  | "feed-card"
  | "thumbnail"
  | "detail-main"
  | "detail-zoom";

/** Appendix A: detail 82, zoom 88; other usages keep caller/default quality. */
const TRANSFORM_QUALITY_BY_USAGE: Partial<Record<ImageUsage, number>> = {
  "detail-main": 82,
  "detail-zoom": 88,
};

const OBJECT_FIT_CLASS_PATTERN =
  /\bobject-(?:contain|cover|fill|none|scale-down)\b/g;

/** Avoid duplicate/conflicting Tailwind fit utilities from callers. */
function stripObjectFitClasses(className: string): string {
  return className
    .replace(OBJECT_FIT_CLASS_PATTERN, "")
    .replace(/\s+/g, " ")
    .trim();
}

interface OptimizedImageProps {
  src: string;
  alt: string;
  width: number;
  height: number;
  className?: string;
  priority?: boolean;
  objectFit?: "contain" | "cover";
  quality?: number;
  onError?: () => void;
  sizes?: string;
  enableBlurPlaceholder?: boolean;
  usage?: ImageUsage;
  /** @deprecated use usage instead */
  mode?: "default" | "strict-feed";
}

export default function OptimizedImage({
  src,
  alt,
  width,
  height,
  className = "",
  priority = false,
  objectFit = "cover",
  quality = 68, // Reduced from 75 to 68 for better performance
  onError,
  sizes = "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw",
  enableBlurPlaceholder = true,
  usage = "default",
  mode,
}: OptimizedImageProps) {
  const [optimizedSrc, setOptimizedSrc] = useState(src);
  const [blurCache, setBlurCache] = useState<{
    src: string;
    data: string;
  } | null>(null);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  /** If transform/render URL fails (e.g. wrong path or plan limits), retry with original Storage URL. */
  const [fallbackSource, setFallbackSource] = useState<string | null>(null);
  const onErrorRef = useRef(onError);
  const useTransformFallback = fallbackSource === src;

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    if (!src) {
      onErrorRef.current?.();
      return;
    }

    const isSupabaseSource =
      typeof src === "string" && src.includes("supabase.co");
    let isMounted = true;

    const effectiveUsage: ImageUsage =
      mode === "strict-feed" && usage === "default" ? "feed-card" : usage;
    const transformQuality =
      TRANSFORM_QUALITY_BY_USAGE[effectiveUsage] ?? quality;

    const updateOptimizedSource = () => {
      if (!isSupabaseSource || useTransformFallback) {
        setOptimizedSrc(src);
        return;
      }

      try {
        const normalizedWidth = Math.max(width, 1);
        const maxTransformWidth =
          effectiveUsage === "detail-main" || effectiveUsage === "detail-zoom"
            ? 1600
            : 600;
        const maxDisplayWidth = Math.min(normalizedWidth, maxTransformWidth);
        const candidates =
          effectiveUsage === "feed-card"
            ? STRICT_FEED_BREAKPOINTS
            : effectiveUsage === "thumbnail"
              ? THUMBNAIL_BREAKPOINTS
              : effectiveUsage === "detail-main"
                ? DETAIL_MAIN_BREAKPOINTS
                : effectiveUsage === "detail-zoom"
                  ? DETAIL_ZOOM_BREAKPOINTS
                  : SIZE_BREAKPOINTS;
        const targetWidth =
          candidates.find((candidate) => candidate >= maxDisplayWidth) ??
          candidates[candidates.length - 1];
        const safeWidth = Math.max(1, targetWidth);
        const aspectRatio = width > 0 ? height / width : 1;
        const derivedHeight = Math.max(1, Math.round(safeWidth * aspectRatio));

        setOptimizedSrc(
          buildSupabaseTransformedUrl(src, {
            width: safeWidth,
            // Contain: width-only transform preserves original aspect (no 4:3 crop box).
            height: objectFit === "contain" ? undefined : derivedHeight,
            quality: transformQuality,
            format: "webp",
            resize: objectFit,
          }),
        );
      } catch (err) {
        console.warn("Failed to transform Supabase URL:", err);
        setOptimizedSrc(src);
      }
    };

    updateOptimizedSource();

    const shouldGenerateBlur =
      enableBlurPlaceholder &&
      effectiveUsage !== "feed-card" &&
      effectiveUsage !== "thumbnail" &&
      !priority &&
      isSupabaseSource &&
      !useTransformFallback &&
      width >= 300;

    if (shouldGenerateBlur) {
      generateBlurDataUrl(src, {
        width: 32,
        quality: 25,
        fit: objectFit,
      })
        .then((blur) => {
          if (isMounted) {
            setBlurCache({ src, data: blur });
          }
        })
        .catch(() => {
          console.warn("Failed to generate blur data URL:", src);
        });
    }

    return () => {
      isMounted = false;
    };
  }, [
    src,
    width,
    height,
    quality,
    priority,
    objectFit,
    useTransformFallback,
    enableBlurPlaceholder,
    usage,
    mode,
  ]);

  const handleError = useCallback(() => {
    if (src.includes("supabase.co") && !useTransformFallback) {
      setFallbackSource(src);
      return;
    }
    setFailedSrc(src);
    onErrorRef.current?.();
  }, [src, useTransformFallback]);

  if (!src || failedSrc === src) {
    return (
      <div
        className={`bg-secondary flex items-center justify-center ${className}`}
        style={{ width, height }}
      >
        <span className="text-text-light">Image not available</span>
      </div>
    );
  }

  // Use unoptimized prop for Supabase-optimized images
  const isPreOptimized =
    typeof optimizedSrc === "string" && optimizedSrc.includes("supabase.co");
  const blurDataUrl = blurCache?.src === src ? blurCache.data : "";
  const layoutClassName = [
    stripObjectFitClasses(className),
    `object-${objectFit}`,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Image
      src={optimizedSrc}
      alt={alt}
      width={width}
      height={height}
      className={layoutClassName}
      loading={priority ? "eager" : "lazy"}
      placeholder={blurDataUrl ? "blur" : "empty"}
      blurDataURL={blurDataUrl}
      priority={priority}
      quality={quality}
      onError={handleError}
      unoptimized={isPreOptimized}
      sizes={sizes}
    />
  );
}
